import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePrevious } from 'react-use';

import { AIOnboardingSuggestionDrawer } from '@/modules/aiSuggestions/components/AIOnboardingSuggestionDrawer';
import { EditClientOrganizationModal } from '@/modules/clientOrganizations/EditClientOrganizationModal';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { EntityValuationActionDefinition } from '@/modules/entities/details/EntityValuation/EntityValuationActions';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getSectionOptions } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { useEditEntityTrowser } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import { TestamentaryEntityModal } from '@/modules/entities/testamentaryEntities/TestamentaryEntityModal';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ViewSourceDrawer } from '@/modules/presentation/clientPresentation/components/ViewSourceDrawer/ViewSourceDrawer';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';

import { useClientPresentationDesignerContext } from '../../ClientPresentationDesigner.context';
import { useUpdateContextData } from '../../ClientPresentationDesigner.hooks';
import { EditEntityModalMount } from './EditEntityModalMount';
import { EditHoldingsMount, EditHoldingsRequest } from './EditHoldingsMount';
import { GroupDetailsDrawer } from './GroupDetailsDrawer';
import {
  GroupDetailsRequest,
  PresentationEntityActionsContextProvider,
  PresentationEntityActionsContextValue,
  PresentationEntityActionTarget,
  ViewSourceRequest,
} from './presentationEntityActions.context';

/**
 * Surface-agnostic core of the entity actions provider. Supplies entity
 * row/diagram-node toolbars with their action callbacks and owns the single
 * shared edit-entity modal (plus the holdings, sub-trust, client-profile,
 * client-org, group-details, and view-source flows). Clicking "Edit entity"
 * sets the targeted entity and mounts the modal against it; closing the modal
 * tears it down and calls `onRefresh` only if an edit was saved, so a pure
 * cancel doesn't trigger a needless refresh. Only one entity occupies the
 * editable context at a time, so switching targets is a remount.
 *
 * `householdId` and the refresh primitive (`onRefresh`) are injected rather
 * than read from a presentation context, so the same provider serves both the
 * Client Presentation Designer (where `onRefresh` is the context-data updater)
 * and the single-entity export preview (where it's the modal's Apollo refetch).
 *
 * Mount this OUTSIDE the slide canvas's `CustomerThemeProvider` where possible
 * so the modal renders in the Luminary app theme; the modal also re-wraps
 * itself in `LuminaryThemeProvider` so a slide-local mount stays Luminary too.
 */
interface PresentationEntityActionsProviderBaseProps {
  /** Household the targeted entities belong to. */
  householdId: string;
  /**
   * How this surface refreshes after a save. Called on edit-modal close when an
   * edit was saved (and unconditionally after holdings/sub-trust close). The
   * designer passes its atomic context-data swap (keeps the rendered data
   * constant until the new response lands — no blanking, no active-slide reset);
   * the preview passes its Apollo refetch.
   */
  onRefresh?: () => void;
  /**
   * Called after the edit-entity / holdings modal closes, in addition to
   * `onRefresh`. Surfaces edits to data the surface's main query doesn't drive —
   * e.g. the entity diagram slide reads its tiles from a separate
   * `useQueryEntityDiagram`, so it passes that query's refetch here. Omitted by
   * callers whose data lives entirely in the main query (entities list,
   * estate waterfall).
   */
  onAfterMutation?: () => void;
  /**
   * Notified whenever an editor (edit-entity, holdings, or sub-trust) opens or
   * closes. Lets a host pause work that shouldn't run mid-edit — e.g. the entity
   * diagram slide holds off its remeasure remount while an editor is open, since
   * a save-and-switch refetch would otherwise remount the diagram behind it.
   */
  onEditorOpenChange?: (open: boolean) => void;
  /**
   * Notified whenever a "View source" drawer opens or closes. Separate from
   * {@link onEditorOpenChange} because it's read-only: a host doesn't need to
   * refresh anything after it, it just needs to know the slide is covered — see
   * the ownership diagram, which skips its remeasure remount while one is open.
   */
  onViewSourceOpenChange?: (open: boolean) => void;
}

export function PresentationEntityActionsProviderBase({
  children,
  householdId,
  onRefresh,
  onAfterMutation,
  onEditorOpenChange,
  onViewSourceOpenChange,
}: PropsWithChildren<PresentationEntityActionsProviderBaseProps>) {
  const { primaryClients, isTwoClientHousehold } = useHouseholdDetailsContext();
  // The edit-entity trowser is URL-driven (its entity id + section live in the
  // URL), so it deep-links, survives reload, and closes on the browser Back
  // button.
  const {
    isOpen: isEditEntityOpen,
    entityId: editEntityId,
    section: editEntitySection,
    openForEntity,
    close: closeEditTrowser,
  } = useEditEntityTrowser();
  const [viewSourceRequest, setViewSourceRequest] =
    useState<ViewSourceRequest | null>(null);
  const [holdingsRequest, setHoldingsRequest] =
    useState<EditHoldingsRequest | null>(null);
  const [subtrustId, setSubtrustId] = useState<string | null>(null);
  const [clientProfileId, setClientProfileId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [groupDetailsRequest, setGroupDetailsRequest] =
    useState<GroupDetailsRequest | null>(null);
  // Bumped per click so re-running the same action on the same entity remounts
  // the holdings flow and re-fires it.
  const holdingsKeyRef = useRef(0);
  // Whether the current edit-entity session saved at least once. Drives the
  // refetch-on-close so a pure cancel (no save) doesn't refetch, while a save
  // still refreshes the slides. Refetching here — on close — rather than inline
  // in the modal's save handler is deliberate: the presentation's server-side
  // data is reliably up to date by the time the modal tears down.
  const editSavedRef = useRef(false);
  // Same save-only-refetch tracking for the client-profile and client-org
  // drawers, which each report a save via their `onAfterSave` callback.
  const clientProfileSavedRef = useRef(false);
  const organizationSavedRef = useRef(false);

  const getEditEntitySections = useCallback(
    (entityType: EntityType) => getSectionOptions(entityType, primaryClients),
    [primaryClients]
  );

  // The edit-entity and edit-holdings flows both mount `EntityDetailsAwareRoute`,
  // which writes the single shared `EntityDetailsContext`. Only one may be
  // active at a time, so starting either clears the other — otherwise two
  // routes race to set `entityId`/subtype and a modal can target the wrong
  // entity.
  const onEditEntitySection = useCallback(
    (target: PresentationEntityActionTarget, section: EditEntitySection) => {
      setHoldingsRequest(null);
      setGroupDetailsRequest(null);
      editSavedRef.current = false;
      openForEntity(target.entityId, { section });
    },
    [openForEntity]
  );

  const onViewSource = useCallback((request: ViewSourceRequest) => {
    setViewSourceRequest(request);
  }, []);

  const closeViewSource = useCallback(() => setViewSourceRequest(null), []);

  const onEditHoldingsAction = useCallback(
    (entityId: string, action: EntityValuationActionDefinition) => {
      closeEditTrowser();
      setGroupDetailsRequest(null);
      holdingsKeyRef.current += 1;
      setHoldingsRequest({ entityId, action, key: holdingsKeyRef.current });
    },
    [closeEditTrowser]
  );

  const onEditSubtrust = useCallback(
    (testamentaryEntityId: string) => {
      // The sub-trust modal owns its own data context, so it doesn't race the
      // shared entity-details flows; still, clear them so only one editor shows.
      closeEditTrowser();
      setHoldingsRequest(null);
      setGroupDetailsRequest(null);
      setSubtrustId(testamentaryEntityId);
    },
    [closeEditTrowser]
  );

  const onEditClientProfile = useCallback(
    (profileId: string) => {
      // The client-profile drawer owns its own data context; clear the shared
      // entity-details flows so only one editor shows.
      closeEditTrowser();
      setHoldingsRequest(null);
      setGroupDetailsRequest(null);
      clientProfileSavedRef.current = false;
      setClientProfileId(profileId);
    },
    [closeEditTrowser]
  );

  const onEditClientOrganization = useCallback(
    (orgId: string) => {
      closeEditTrowser();
      setHoldingsRequest(null);
      setGroupDetailsRequest(null);
      organizationSavedRef.current = false;
      setOrganizationId(orgId);
    },
    [closeEditTrowser]
  );

  // Read-only: just show the members. No shared data context to clear and no
  // refetch on close — but clear the editors so the drawer doesn't linger
  // alongside one opened right after from another node.
  const onShowGroupDetails = useCallback(
    (request: GroupDetailsRequest) => {
      closeEditTrowser();
      setHoldingsRequest(null);
      setSubtrustId(null);
      setClientProfileId(null);
      setOrganizationId(null);
      setGroupDetailsRequest(request);
    },
    [closeEditTrowser]
  );

  // A save occurred; remember it so the close handler refreshes. We defer the
  // refresh to close so multiple section saves coalesce into a single update.
  const onEditSaved = useCallback(() => {
    editSavedRef.current = true;
  }, []);

  // Refresh on close (whether via a button or Back — both flip the URL open
  // flag) if something was saved. Keying off the flag covers a Back-close too.
  const wasEditEntityOpen = usePrevious(isEditEntityOpen);
  useEffect(() => {
    if (wasEditEntityOpen && !isEditEntityOpen && editSavedRef.current) {
      editSavedRef.current = false;
      void onRefresh?.();
      onAfterMutation?.();
    }
  }, [wasEditEntityOpen, isEditEntityOpen, onRefresh, onAfterMutation]);

  const closeHoldings = useCallback(() => {
    setHoldingsRequest(null);
    // Refresh unconditionally on close (unlike the edit-entity flow, which gates
    // on a save). The holdings modals signal completion through `onClose` alone —
    // several successful saves (add-liability, GRAT valuation, holdings-opened
    // entity edit) never fire a distinct save callback, so gating on one would
    // drop those refreshes and leave the slides stale. A refresh on a pure cancel
    // is harmless here: the holdings flow is only reachable on the entities-list
    // and entity-cover slides, whose provider passes no `onAfterMutation`, so the
    // diagram-remounting refetch never runs off this path.
    void onRefresh?.();
    onAfterMutation?.();
  }, [onRefresh, onAfterMutation]);

  const closeSubtrust = useCallback(() => {
    setSubtrustId(null);
    // The sub-trust modal already refetches active queries on save (refreshing
    // any diagram it appears on), so just refresh via the surface's primitive.
    void onRefresh?.();
  }, [onRefresh]);

  const closeClientProfile = useCallback(() => {
    setClientProfileId(null);
    // Only refresh if something was saved — a pure cancel changes nothing.
    if (clientProfileSavedRef.current) {
      clientProfileSavedRef.current = false;
      void onRefresh?.();
      onAfterMutation?.();
    }
  }, [onRefresh, onAfterMutation]);

  const closeClientOrganization = useCallback(() => {
    setOrganizationId(null);
    if (organizationSavedRef.current) {
      organizationSavedRef.current = false;
      void onRefresh?.();
      onAfterMutation?.();
    }
  }, [onRefresh, onAfterMutation]);

  // Report whether a mutating editor is open. The view-source drawers are
  // read-only, so they don't count here — they report separately below.
  const isEditorOpen =
    isEditEntityOpen ||
    !!holdingsRequest ||
    !!subtrustId ||
    !!clientProfileId ||
    !!organizationId;
  useEffect(() => {
    onEditorOpenChange?.(isEditorOpen);
  }, [isEditorOpen, onEditorOpenChange]);

  const isViewSourceOpen = !!viewSourceRequest;
  useEffect(() => {
    onViewSourceOpenChange?.(isViewSourceOpen);
  }, [isViewSourceOpen, onViewSourceOpenChange]);

  const value = useMemo<PresentationEntityActionsContextValue>(
    () => ({
      householdId,
      getEditEntitySections,
      onEditEntitySection,
      onEditHoldingsAction,
      onViewSource,
      onEditSubtrust,
      onEditClientProfile,
      onEditClientOrganization,
      onShowGroupDetails,
    }),
    [
      householdId,
      getEditEntitySections,
      onEditEntitySection,
      onEditHoldingsAction,
      onViewSource,
      onEditSubtrust,
      onEditClientProfile,
      onEditClientOrganization,
      onShowGroupDetails,
    ]
  );

  return (
    <PresentationEntityActionsContextProvider value={value}>
      {children}
      {/* These mounts must render in the Luminary app theme, but this provider
          can be mounted under a slide's CustomerThemeProvider (e.g. the estate
          waterfall diagram slide). Force Luminary here so the edit modals,
          holdings flows, and source viewer don't inherit the tenant brand. */}
      {isEditEntityOpen && editEntityId && (
        <LuminaryThemeProvider>
          <EditEntityModalMount
            entityId={editEntityId}
            section={editEntitySection ?? undefined}
            onClose={closeEditTrowser}
            onAfterSave={onEditSaved}
          />
        </LuminaryThemeProvider>
      )}
      {/* The requested citation: the suggestion's highlighted evidence, or an
          entity's source documents. Which one is decided by the caller (see
          `ViewSourceRequest`), not inferred here. */}
      {viewSourceRequest?.kind === 'suggestion' && (
        <LuminaryThemeProvider>
          <AIOnboardingSuggestionDrawer
            isOpen
            suggestionId={viewSourceRequest.suggestionId}
            onClose={closeViewSource}
          />
        </LuminaryThemeProvider>
      )}
      {viewSourceRequest?.kind === 'sourceDocuments' && (
        <LuminaryThemeProvider>
          <ViewSourceDrawer
            householdId={householdId}
            entityId={viewSourceRequest.entityId}
            onClose={closeViewSource}
          />
        </LuminaryThemeProvider>
      )}
      {holdingsRequest && (
        <LuminaryThemeProvider>
          <EditHoldingsMount
            key={holdingsRequest.key}
            entityId={holdingsRequest.entityId}
            action={holdingsRequest.action}
            onClose={closeHoldings}
          />
        </LuminaryThemeProvider>
      )}
      {subtrustId && (
        <LuminaryThemeProvider>
          <TestamentaryEntityModal
            isOpen
            testamentaryEntityId={subtrustId}
            isTwoClientHousehold={isTwoClientHousehold}
            onClose={closeSubtrust}
          />
        </LuminaryThemeProvider>
      )}
      {clientProfileId && (
        <LuminaryThemeProvider>
          <ClientProfileSideDrawer
            isOpen
            setIsOpen={(open) => {
              // The drawer only ever asks to close (it flips this false on
              // save/cancel/delete); route that through our save-aware close.
              if (!open) closeClientProfile();
            }}
            householdId={householdId}
            clientProfileId={clientProfileId}
            // Primary clients can't be deleted and must stay grantors; flag them
            // so the drawer locks those controls.
            isPrimary={primaryClients?.some((c) => c.id === clientProfileId)}
            clientProfileTypeDisplay="individual"
            onAfterSave={() => {
              clientProfileSavedRef.current = true;
            }}
          />
        </LuminaryThemeProvider>
      )}
      {organizationId && (
        <LuminaryThemeProvider>
          <EditClientOrganizationModal
            isOpen
            organizationId={organizationId}
            householdId={householdId}
            onClose={closeClientOrganization}
            onAfterSave={() => {
              organizationSavedRef.current = true;
            }}
          />
        </LuminaryThemeProvider>
      )}
      {groupDetailsRequest && (
        <LuminaryThemeProvider>
          <GroupDetailsDrawer
            householdId={householdId}
            members={groupDetailsRequest.members}
            onClose={() => setGroupDetailsRequest(null)}
          />
        </LuminaryThemeProvider>
      )}
    </PresentationEntityActionsContextProvider>
  );
}

type PresentationEntityActionsProviderProps = Omit<
  PresentationEntityActionsProviderBaseProps,
  'householdId' | 'onRefresh'
>;

/**
 * Designer adapter for {@link PresentationEntityActionsProviderBase}. Feeds
 * `householdId` from the presentation designer context and refreshes via the
 * context-data updater — which keeps the rendered `contextData` constant until
 * the new response lands, then swaps it in atomically, so slides update in
 * place without blanking or resetting the active slide. Existing designer call
 * sites use this; the single-entity export mounts the base directly.
 */
export function PresentationEntityActionsProvider({
  children,
  ...props
}: PropsWithChildren<PresentationEntityActionsProviderProps>) {
  const { householdId } = useClientPresentationDesignerContext();
  const { updateContextData } = useUpdateContextData();

  return (
    <PresentationEntityActionsProviderBase
      householdId={householdId}
      onRefresh={() => {
        void updateContextData();
      }}
      {...props}
    >
      {children}
    </PresentationEntityActionsProviderBase>
  );
}
